import { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import OnboardingDialog from '../../../common/onboarding-dialog';
import l from '../../../../lang';
import { Routes } from '../../../../config/routes';
import { usePayouts } from '../../../../store/payouts';
import { useDashboard } from '../../../../store/dashboard';
import { useCompanyInfo } from '../../../../store/company-information';
import { MessageTypes } from '../../../../constants';
import {
  BeneficiaryBankAccountType,
  BeneficiaryRelationship,
  FlowType,
  PayoutCreationStep,
} from '../../../../store/payouts/interfaces';
import { ErrorInfo } from '../../../../utils/error-codes';
import MessageError from '../../../common/message-error';
import Loading from '../../../common/loading';
import CountrySelection from '../create-steps/account-data/country-selection';
import BankAccountForm from '../create-steps/account-data/bank-account-form';
import DialogOtpNotSet from '../create-steps/dialog-otp-not-set';
import { getPayoutWizardMenuItems, mapFieldPathValuesToCreatePayoutData } from '../create-steps/utils';
import { mapFormToCreateBeneficiaryRequest } from './mappers';
import { BeneficiaryAccountMode, BeneficiaryAccountPageProps, BeneficiaryFieldValues } from './types';

const isBankAccountMode = (mode: BeneficiaryAccountMode) => mode === 'ADD_BANK_ACCOUNT' || mode === 'EDIT_BANK_ACCOUNT';
import { useBeneficiaryAccountFlow } from './use-beneficiary-account-flow';
import '../create-steps/styles.scss';

const parseErrorInfo = (error: any): ErrorInfo | undefined => {
  const body = error?.body ?? error;
  if (body?.errorInfo) {
    return body.errorInfo;
  }
  if (body?.errorMessage) {
    return {
      level: MessageTypes.ERROR,
      message: 'payouts.create.error.providerRejected',
      messageArgs: [body.errorMessage],
    };
  }
  return undefined;
};

const parseErrorFields = (error: any, values: BeneficiaryFieldValues): Record<string, string> | undefined => {
  const fields = error?.body?.errorFields ?? error?.errorFields;
  if (!fields) {
    return undefined;
  }
  const paths = Array.isArray(fields) ? fields : Object.keys(fields);
  return paths.reduce<Record<string, string>>((result, path) => {
    if (typeof path === 'string' && values[path] !== undefined) {
      result[path] = String(values[path]);
    }
    return result;
  }, {});
};

const BeneficiaryAccountPage = ({ mode }: BeneficiaryAccountPageProps) => {
  const history = useHistory();
  const { beneficiary_id: beneficiaryId, account_id: accountId } = useParams<{
    beneficiary_id?: string;
    account_id?: string;
  }>();
  const { merchantMe, setShowTwoFactorAuthSetup } = useDashboard();
  const {
    createPayout,
    contactsList,
    isOtpConfigured,
    startCreatePayout,
    setCreatePayoutData,
    setCreatePayoutStep,
    validatePartialPayout,
    verifyOtpConfigured,
    getPayoutContacts,
    getPayoutContactsList,
    createPayoutBeneficiary,
    addPayoutBeneficiaryBankAccount,
    updatePayoutBeneficiaryBankAccount,
  } = usePayouts();
  const { documentTypesForCompany, documentTypesForNaturalPerson, getDocumentTypesForKYC } = useCompanyInfo();
  const [hasRequestedContacts, setHasRequestedContacts] = useState(contactsList.data.length > 0);
  const [submitting, setSubmitting] = useState(false);
  const [submissionFailed, setSubmissionFailed] = useState(false);
  const [submitError, setSubmitError] = useState<ErrorInfo>();
  const [backendErrorFields, setBackendErrorFields] = useState<Record<string, string>>();
  const [pendingPayoutValues, setPendingPayoutValues] = useState<BeneficiaryFieldValues>();

  const contact = useMemo(
    () => contactsList.data.find(item => String(item.id) === beneficiaryId),
    [beneficiaryId, contactsList.data]
  );
  const bankAccount = useMemo(
    () => contact?.bank_accounts?.find(account => String(account.id) === accountId),
    [accountId, contact]
  );
  const flow = useBeneficiaryAccountFlow({ mode, contact, bankAccount });

  useEffect(() => {
    if (mode === 'PAYOUT') {
      if (createPayout.step === PayoutCreationStep.NONE) {
        startCreatePayout();
      }
      setCreatePayoutStep(PayoutCreationStep.ACCOUNT_DATA);
      verifyOtpConfigured();
    }
  }, [mode]);

  useEffect(() => {
    if (merchantMe?.id && !contactsList.data.length && !hasRequestedContacts) {
      setHasRequestedContacts(true);
      getPayoutContactsList(String(merchantMe.id));
    }
  }, [contactsList.data.length, hasRequestedContacts, merchantMe?.id]);

  useEffect(() => {
    if (!isBankAccountMode(mode) && flow.country && flow.country !== 'CL') {
      getDocumentTypesForKYC(flow.country);
    }
  }, [flow.country, mode]);

  useEffect(() => {
    if (isBankAccountMode(mode) && beneficiaryId && hasRequestedContacts && !contactsList.loading && !contact) {
      history.replace(Routes.PAYOUT_CONTACTS);
    }
  }, [beneficiaryId, contact, contactsList.loading, hasRequestedContacts, history, mode]);

  useEffect(() => {
    if (
      mode === 'EDIT_BANK_ACCOUNT' &&
      beneficiaryId &&
      contact &&
      hasRequestedContacts &&
      !contactsList.loading &&
      !bankAccount
    ) {
      history.replace(Routes.PAYOUT_CONTACT_DETAIL.replace(':contact_id', beneficiaryId));
    }
  }, [bankAccount, beneficiaryId, contact, contactsList.loading, hasRequestedContacts, history, mode]);

  const listRoute = Routes.PAYOUT_CONTACTS;
  const detailRoute = beneficiaryId ? Routes.PAYOUT_CONTACT_DETAIL.replace(':contact_id', beneficiaryId) : listRoute;
  const relationshipOptions: Record<string, string> =
    mode === 'CREATE_CONTACT' || mode === 'PAYOUT'
      ? {
          [BeneficiaryRelationship.SUPPLIER]: l('payoutContacts.relationship.SUPPLIER'),
          [BeneficiaryRelationship.EMPLOYEE]: l('payoutContacts.relationship.EMPLOYEE'),
          [BeneficiaryRelationship.CONTRACTOR]: l('payoutContacts.relationship.CONTRACTOR'),
          [BeneficiaryRelationship.FAMILY]: l('payoutContacts.relationship.FAMILY'),
          [BeneficiaryRelationship.FRIEND]: l('payoutContacts.relationship.FRIEND'),
          [BeneficiaryRelationship.OTHER]: l('payoutContacts.relationship.OTHER'),
        }
      : {};
  const cancelRoute =
    mode === 'PAYOUT' ? Routes.PAYOUTS_STEP_PAYOUT_DATA : isBankAccountMode(mode) ? detailRoute : listRoute;

  const handleOtpDialog = (confirm: boolean) => {
    if (confirm) {
      setShowTwoFactorAuthSetup(true);
    } else {
      history.push(Routes.PAYOUTS);
    }
  };

  const finishWithError = (error: any, values: BeneficiaryFieldValues) => {
    setSubmitting(false);
    setSubmissionFailed(true);
    setSubmitError(parseErrorInfo(error));
    setBackendErrorFields(parseErrorFields(error, values));
  };

  const submitCreatedBeneficiary = (values: BeneficiaryFieldValues) => {
    if (!merchantMe?.id) {
      return;
    }
    setSubmitting(true);
    setSubmissionFailed(false);
    setSubmitError(undefined);
    setBackendErrorFields(undefined);
    const request = mapFormToCreateBeneficiaryRequest({
      values,
      country: flow.country,
      flowType: flow.flowType,
      currency: flow.defaultCurrency,
      bankOptions: flow.bankOptions,
    });

    createPayoutBeneficiary(
      String(merchantMe.id),
      request,
      response => {
        if (mode === 'PAYOUT') {
          const responseData = response as any;
          const account = responseData.bank_accounts?.find((item: any) => item.preferred) ?? responseData.bank_accounts?.[0];
          setCreatePayoutData({
            ...mapFieldPathValuesToCreatePayoutData(values),
            transferCountry: flow.country,
            flowType: flow.flowType,
            currencyToPay: request.bank_account.currency,
            bankAccount: request.bank_account.bank_account,
            bankAccountId: account?.id ?? responseData.bank_account_id,
          });
          getPayoutContacts(String(merchantMe.id));
          setCreatePayoutStep(PayoutCreationStep.PAYOUT_DATA);
          history.push(Routes.PAYOUTS_STEP_PAYOUT_DATA);
        } else {
          getPayoutContactsList(String(merchantMe.id));
          getPayoutContacts(String(merchantMe.id));
          history.push(Routes.PAYOUT_CONTACTS);
        }
        setSubmitting(false);
      },
      error => finishWithError(error, values)
    );
  };

  useEffect(() => {
    if (mode === 'PAYOUT' && pendingPayoutValues && createPayout.partialValidation.success && !submitting) {
      const values = pendingPayoutValues;
      setPendingPayoutValues(undefined);
      submitCreatedBeneficiary(values);
    }
  }, [createPayout.partialValidation.success, mode, pendingPayoutValues, submitting]);

  const refreshAndReturnToDetail = () => {
    if (!merchantMe?.id) {
      return;
    }
    getPayoutContactsList(String(merchantMe.id));
    getPayoutContacts(String(merchantMe.id));
    setSubmitting(false);
    history.push(detailRoute);
  };

  const submitBankAccount = (values: BeneficiaryFieldValues) => {
    if (!merchantMe?.id || !beneficiaryId || (mode === 'EDIT_BANK_ACCOUNT' && !accountId)) {
      return;
    }
    setSubmitting(true);
    setSubmissionFailed(false);
    setSubmitError(undefined);
    setBackendErrorFields(undefined);
    const bankCode = String(values['beneficiary.bank_account.code'] || values['beneficiary.bank_name'] || '').trim();
    const request = {
      preferred: mode === 'EDIT_BANK_ACCOUNT' ? (bankAccount?.preferred ?? false) : false,
      country: flow.country,
      currency: String(values.currency_to_pay || values['beneficiary.bank_account.currency'] || flow.defaultCurrency).trim(),
      bank_code: bankCode,
      bank_name: flow.bankOptions.find(bank => String(bank.code) === bankCode)?.name ?? bankCode,
      bank_account: String(values['beneficiary.bank_account.account'] ?? '').trim(),
      bank_branch: String(values['beneficiary.bank_account.branch'] ?? '').trim(),
      account_type: String(values['beneficiary.bank_account.type'] ?? '').trim() as BeneficiaryBankAccountType,
    };

    if (mode === 'EDIT_BANK_ACCOUNT' && accountId) {
      updatePayoutBeneficiaryBankAccount(
        String(merchantMe.id),
        beneficiaryId,
        accountId,
        request,
        refreshAndReturnToDetail,
        error => finishWithError(error, values)
      );
      return;
    }

    addPayoutBeneficiaryBankAccount(String(merchantMe.id), beneficiaryId, request, refreshAndReturnToDetail, error =>
      finishWithError(error, values)
    );
  };

  const handleSubmit = (values: BeneficiaryFieldValues) => {
    flow.setFieldValues(values);
    if (mode === 'PAYOUT') {
      setSubmissionFailed(false);
      setSubmitError(undefined);
      setBackendErrorFields(undefined);
      setCreatePayoutData({
        ...mapFieldPathValuesToCreatePayoutData(values),
        transferCountry: flow.country,
        flowType: flow.flowType,
      });
      setPendingPayoutValues(values);
      validatePartialPayout(values);
    } else if (isBankAccountMode(mode)) {
      submitBankAccount(values);
    } else {
      submitCreatedBeneficiary(values);
    }
  };

  const title =
    mode === 'PAYOUT'
      ? `${l('payouts.create.title')} | ${l('payouts.create.accountData.menuTitle')}`
      : mode === 'CREATE_CONTACT'
        ? l('payoutContacts.create.title')
        : mode === 'EDIT_BANK_ACCOUNT'
          ? l('payoutContacts.editAccount.title')
          : l('payoutContacts.addAccount.title');

  const formContent =
    (isBankAccountMode(mode) && !contact) || (mode === 'EDIT_BANK_ACCOUNT' && !bankAccount) ? (
      <Loading />
    ) : flow.step === 'country' ? (
      <CountrySelection
        country={flow.country}
        flowType={flow.flowType}
        enabledCountryConfigurations={flow.enabledCountryConfigurations.data}
        loadingConfigurations={flow.enabledCountryConfigurations.loading}
        onCountryChange={flow.setCountry}
        onFlowTypeChange={flow.setFlowType}
        onCancel={() => history.push(cancelRoute)}
        onNext={flow.openBankData}
      />
    ) : (
      <BankAccountForm
        loading={flow.loading}
        validating={submitting || (mode === 'PAYOUT' && createPayout.partialValidation.loading)}
        fields={flow.fields}
        bankOptions={flow.bankOptions}
        initialValues={mode === 'PAYOUT' ? flow.initialPayoutData : {}}
        initialFieldValues={flow.fieldValues}
        country={flow.country}
        flowType={flow.flowType}
        currencyOptions={flow.currencyOptions}
        merchantCountry={merchantMe?.country}
        documentTypesForCompany={documentTypesForCompany}
        documentTypesForNaturalPerson={documentTypesForNaturalPerson}
        relationshipOptions={relationshipOptions}
        errorInfo={
          mode === 'PAYOUT' && createPayout.partialValidation.error ? createPayout.partialValidation.errorInfo : submitError
        }
        validationError={submissionFailed || (mode === 'PAYOUT' && createPayout.partialValidation.error)}
        backendErrorFields={
          mode === 'PAYOUT' && createPayout.partialValidation.error
            ? createPayout.partialValidation.errorFields
            : backendErrorFields
        }
        accountNumberDisabled={mode === 'EDIT_BANK_ACCOUNT'}
        existingContactEmails={contactsList.data.map(item => item.email || '').filter(Boolean)}
        submitLabel={
          mode === 'CREATE_CONTACT'
            ? l('payoutContacts.create.submit')
            : mode === 'ADD_BANK_ACCOUNT'
              ? l('payoutContacts.addAccount.submit')
              : mode === 'EDIT_BANK_ACCOUNT'
                ? l('payoutContacts.editAccount.submit')
                : l('common.next')
        }
        onBack={() => {
          if (isBankAccountMode(mode)) {
            history.push(detailRoute);
          } else {
            flow.backToCountry();
          }
        }}
        onSubmit={handleSubmit}
      />
    );
  const body = (
    <>
      {flow.loadError && <MessageError errorInfo={parseErrorInfo(flow.loadError)} />}
      {formContent}
    </>
  );

  return (
    <>
      {!isOtpConfigured && <DialogOtpNotSet onClose={handleOtpDialog} />}
      <OnboardingDialog
        dividers={false}
        unpaddingContent
        unpaddingFooter
        fullScreenMobile
        type="md"
        bgColor="#F9FAFB"
        title={title}
        body={body}
        handleClose={() => history.push(cancelRoute)}
        menuItems={mode === 'PAYOUT' ? getPayoutWizardMenuItems('account') : []}
      />
    </>
  );
};

export default BeneficiaryAccountPage;
